import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import './public-site.css';
import logoUrl from './assets/icons/logo.svg';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.tegratauto.com';
const INSTAGRAM_PROFILE_URL = 'https://www.instagram.com/tegratauto/';
const INSTAGRAM_EMBED_SCRIPT = 'https://www.instagram.com/embed.js';
const IOS_APP_URL = 'https://apps.apple.com/us/app/tegrat-auto/id6808904419';

// True for iPhone/iPad/iPod, including iPadOS which reports itself as a Mac.
function isIOSDevice() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  return /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
}

// ── Minimal SVG icon helper ──────────────────────────────────
const I = ({ children, size = 18, sw = 1.7 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);
const IWrench  = (p) => <I {...p}><path d="M14.7 6.3a4 4 0 1 1 3 3l-9 9a2.1 2.1 0 0 1-3-3l9-9z"/></I>;
const ICar     = (p) => <I {...p}><path d="M5 17h14M3 17v-4l3-6h12l3 6v4M7 17v2M17 17v2"/><circle cx="8" cy="13.5" r="1.2" fill="currentColor"/><circle cx="16" cy="13.5" r="1.2" fill="currentColor"/></I>;
const IBriefcase=(p)=> <I {...p}><rect x="3" y="7" width="18" height="13" rx="2"/><path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/></I>;
const IGlobe   = (p) => <I {...p}><circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18"/></I>;
const IArrowR  = (p) => <I {...p}><path d="M5 12h14M13 6l6 6-6 6"/></I>;
const ICheck   = (p) => <I {...p}><path d="M5 12l5 5 9-10"/></I>;
const IShield  = (p) => <I {...p}><path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6l8-3z"/><path d="M9 12l2 2 4-4"/></I>;
const ISparkle = (p) => <I {...p}><path d="M12 3v4M12 17v4M3 12h4M17 12h4M6 6l3 3M15 15l3 3M6 18l3-3M15 9l3-3"/></I>;
const IPhone   = (p) => <I {...p}><path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A15 15 0 0 1 3 6a2 2 0 0 1 2-2z"/></I>;
const IClock   = (p) => <I {...p}><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></I>;
const IMail    = (p) => <I {...p}><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 7l9 7 9-7"/></I>;
const IInsta   = (p) => <I {...p}><rect x="3" y="3" width="18" height="18" rx="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r="1" fill="currentColor"/></I>;
const IDollar  = (p) => <I {...p}><path d="M12 3v18M16 7H10a3 3 0 0 0 0 6h4a3 3 0 0 1 0 6H8"/></I>;
const IZap     = (p) => <I {...p}><path d="M13 3L4 14h7l-1 7 9-11h-7l1-7z"/></I>;
const IBuilding= (p) => <I {...p}><path d="M3 21h18M5 21V7l7-4 7 4v14M9 21v-4h6v4M9 11h.01M15 11h.01M9 14h.01M15 14h.01"/></I>;

// ── Invest / Partner section ─────────────────────────────────
const INVEST_OPTIONS = [
  {
    tag: 'Passive income',
    ic: <ICar size={26}/>,
    icClass: '',
    title: 'List your cars with us',
    sub: 'Put idle vehicles to work.',
    body: "Own one car or a whole fleet? List your vehicles on the Tegrat Auto platform and we put them in front of pre-screened, ready-to-drive customers. You earn for every week they're on the road — we handle everything else.",
    points: [
      'We manage screening, rentals & collections',
      'Maintenance and insurance claims handled',
      'Transparent weekly payouts to you',
    ],
    mailto: 'join@tegratauto.com',
    subject: "I'd like to list my car(s) with Tegrat Auto",
    altCard: false,
  },
  {
    tag: 'Own a territory',
    ic: <IBuilding size={26}/>,
    icClass: 'invest-ico-teal',
    title: 'Operate a branch',
    sub: 'Bring Tegrat Auto to your city.',
    body: "Join us as a branch operator and run Tegrat Auto in your own market. You get the playbook, the platform, and the brand behind you — we help you launch, source inventory, and grow a profitable local hub.",
    points: [
      'Proven operating model & tools',
      'Onboarding, training & ongoing support',
      'Shared inventory and financing network',
    ],
    mailto: 'join@tegratauto.com',
    subject: "I'm interested in operating a Tegrat Auto branch",
    altCard: true,
  },
];

function InvestSection() {
  return (
    <section className="wrapper style1 align-center" id="invest">
      <div className="inner invest-section">
        <h2>Two Ways to Invest with Us</h2>
        <p className="major">
          Have cars sitting idle, or the drive to run your own hub? Partner with Tegrat Auto
          and earn alongside a proven rent-to-own model.
        </p>

        <div className="invest-cards-grid">
          {INVEST_OPTIONS.map((opt) => (
            <article key={opt.title} className={`invest-card-item${opt.altCard ? ' invest-card-alt' : ''}`}>
              <div className="invest-card-header">
                <div className={`invest-ico-wrap${opt.icClass ? ` ${opt.icClass}` : ''}`}>
                  {opt.ic}
                </div>
                <span className="invest-badge">{opt.tag}</span>
              </div>
              <h3>{opt.title}</h3>
              <p className="invest-card-sub">{opt.sub}</p>
              <p>{opt.body}</p>
              <ul className="invest-points">
                {opt.points.map((pt) => <li key={pt}>{pt}</li>)}
              </ul>
              <a
                href={`mailto:${opt.mailto}?subject=${encodeURIComponent(opt.subject)}`}
                className="invest-cta-link"
              >
                Email us about this <IArrowR size={15}/>
              </a>
            </article>
          ))}
        </div>

        <p className="invest-contact-line">
          Questions before you start? Reach the partnerships team at{' '}
          <a href="mailto:join@tegratauto.com">join@tegratauto.com</a>
        </p>

        <div className="invest-fineprint">
          <div className="invest-fineprint-label">Fine print</div>
          <p>
            [ Placeholder — replace this block with your investor terms and disclosures. ]
            Participation in any listing or branch-operator program is subject to a separate written
            agreement, eligibility review, and applicable state and federal law. Earnings figures are
            illustrative and not guaranteed; all investment involves risk, including possible loss of
            capital. This page is informational only and does not constitute an offer to sell or a
            solicitation of an offer to buy any security or franchise.
          </p>
        </div>
      </div>
    </section>
  );
}

// ── Nav ──────────────────────────────────────────────────────
const NAV_ITEMS = [
  { id: 'services',     label: 'Services' },
  { id: 'pricing',      label: 'Pricing' },
  { id: 'requirements', label: 'Requirements' },
  { id: 'locations',    label: 'Locations' },
];

function Nav() {
  const [active, setActive] = useState('');
  const isIOS = isIOSDevice();

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        const inView = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (inView) setActive(inView.target.id);
      },
      { rootMargin: '-30% 0px -55% 0px', threshold: [0.1, 0.5] }
    );
    NAV_ITEMS.forEach((i) => {
      const el = document.getElementById(i.id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, []);

  return (
    <nav className={`ps-nav${isIOS ? ' ps-nav-ios' : ''}`} aria-label="Primary">
      <a href="#top" className="ps-nav-brand">
        <img src={logoUrl} alt="" className="ps-nav-logo" width="32" height="32" />
        Tegrat Auto
      </a>
      <div className="ps-nav-links">
        {NAV_ITEMS.map((i) => (
          <a
            key={i.id}
            className={`ps-nav-link${active === i.id ? ' active' : ''}`}
            href={`#${i.id}`}
          >
            {i.label}
          </a>
        ))}
      </div>
      <div className="ps-nav-actions">
        <a href="#contact" className="ps-nav-cta">
          Get a car <IArrowR size={15} />
        </a>
        {isIOS && (
          <a href={IOS_APP_URL} className="ps-nav-cta ps-nav-app" aria-label="Get Tegrat Auto on the App Store">
            Get the iOS app
          </a>
        )}
      </div>
      <button className="ps-nav-burger" aria-label="Menu">
        <I><path d="M4 7h16M4 12h16M4 17h16" /></I>
      </button>
    </nav>
  );
}

// ── Hero ─────────────────────────────────────────────────────
function HeroVisual() {
  useEffect(() => {
    const processInstagramEmbed = () => window.instgrm?.Embeds?.process();
    const existingScript = document.querySelector(`script[src="${INSTAGRAM_EMBED_SCRIPT}"]`);

    if (existingScript) {
      processInstagramEmbed();
      existingScript.addEventListener('load', processInstagramEmbed);
      return () => existingScript.removeEventListener('load', processInstagramEmbed);
    }

    const script = document.createElement('script');
    script.src = INSTAGRAM_EMBED_SCRIPT;
    script.async = true;
    script.addEventListener('load', processInstagramEmbed);
    document.body.appendChild(script);

    return () => script.removeEventListener('load', processInstagramEmbed);
  }, []);

  return (
    <div className="instagram-visual">
      <div className="instagram-visual-header">
        <div className="instagram-visual-title">
          <IInsta size={20} />
          <span>Follow us on Instagram</span>
        </div>
        <a href={INSTAGRAM_PROFILE_URL} target="_blank" rel="noopener noreferrer">
          @tegratauto <IArrowR size={14} />
        </a>
      </div>
      <div className="instagram-embed-shell">
        <blockquote
          className="instagram-media instagram-profile-embed"
          data-instgrm-permalink={INSTAGRAM_PROFILE_URL}
          data-instgrm-version="14"
        >
          <a href={INSTAGRAM_PROFILE_URL} target="_blank" rel="noopener noreferrer">
            View @tegratauto on Instagram
          </a>
        </blockquote>
      </div>
    </div>
  );
}

function Hero() {
  return (
    <header className="hero" id="top">
      <div className="hero-inner">
        <div>
          <span className="eyebrow">
            <span className="e-dot"><span className="e-pulse" /></span>
            No credit check · Drive today
          </span>
          <h1 className="h-display">
            Car problems?<br />Credit issues? <em>We have your keys.</em>
          </h1>
          <p className="h-lede">
            Whether your car is in the shop, you've faced a recent repossession, or you're an
            international student needing reliable wheels — Tegrat Auto gets you back on the road
            today. Reliable cars and a path to ownership.
          </p>
          <div className="h-actions">
            <a href="#locations" className="btn-blue btn-primary">
              View our locations <IArrowR size={15} />
            </a>
            <a href="#contact" className="btn-ghost">
              Tell us what you need
            </a>
          </div>
          <div className="h-trust">
            <span><strong>5 hubs</strong> across the US</span>
            <span className="sep" />
            <span><strong>15 min</strong> approval</span>
            <span className="sep" />
            <span>Uber · Lyft · DoorDash <strong>welcome</strong></span>
          </div>
        </div>
        <HeroVisual />
      </div>
    </header>
  );
}

// ── Marquee ──────────────────────────────────────────────────
const MARQUEE_ITEMS = [
  { icon: <ICheck size={16} />, text: 'No credit check' },
  { icon: <ICar size={16} />, text: 'Rent · Lease · Lease Buy-out' },
  { icon: <IShield size={16} />, text: 'Maintenance covered' },
  { icon: <IGlobe size={16} />, text: 'International licenses accepted' },
  { icon: <IClock size={16} />, text: 'Approved in 15 minutes' },
  { icon: <IDollar size={16} />, text: 'Wholesale buy-out prices' },
];

function Marquee() {
  const items = [...MARQUEE_ITEMS, ...MARQUEE_ITEMS];
  return (
    <div className="marquee">
      <div className="marquee-track">
        {items.map((x, i) => (
          <div key={i} className="marquee-item">
            {x.icon}<span>{x.text}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Services (4 cards) ───────────────────────────────────────
const HOOKS = [
  {
    n: '01', accent: '', icon: <IWrench size={22} />,
    title: 'Car broken down?',
    sub: "Don’t let a mechanical failure stop your life.",
    body: (<>Being without a vehicle is more than an inconvenience — it&#39;s a loss of income and freedom. Bring us your mechanic&#39;s quote and <strong>we&#39;ll match or beat it</strong>, plus put you in a high-quality loaner at our competitive weekly rates.</>),
    cta: 'Get a loaner today',
  },
  {
    n: '02', accent: 'amber', icon: <IBriefcase size={22} />,
    title: 'Repo or credit issues?',
    sub: 'Income opportunity, not a denial letter.',
    body: (<>Life happens. We say "Yes" where traditional dealerships say no — and your ability to work is your best asset. <strong>Uber, Lyft, and DoorDash</strong> drivers welcome. Turn your weekly payment into a job.</>),
    cta: 'Start earning',
  },
  {
    n: '03', accent: 'teal', icon: <ICar size={22} />,
    title: 'Looking at Buy Here Pay Here?',
    sub: 'Try before you buy.',
    body: (<>Most dealerships jack up prices to pay salespeople. We don't. Because we earn on rentals, you buy at <strong>wholesale prices</strong> — saving thousands. Rent the exact car first to make sure it's not a lemon.</>),
    cta: 'Start your journey',
  },
  {
    n: '04', accent: 'rose', icon: <IGlobe size={22} />,
    title: 'Internship or international license?',
    sub: 'New to the country? Take your time to settle.',
    body: (<>Tegrat Auto specialises in <strong>3–6 month flexible leases</strong> for international students, seasonal interns and short-term workers. International driver's licenses accepted. <strong>Student discounts</strong> available.</>),
    cta: 'Get student pricing',
  },
];

function Services() {
  return (
    <section id="services" className="ps-section">
      <div className="section-inner">
        <div className="section-head">
          <div>
            <span className="eyebrow"><span className="e-dot">✦</span>How we serve you</span>
            <h2>Four ways into the<br />driver's seat — <em>today</em>.</h2>
          </div>
          <p className="lede">Pick the situation that sounds like yours. We've built a path for each one.</p>
        </div>
        <div className="hooks-grid">
          {HOOKS.map((h) => (
            <article key={h.n} className="hook">
              <div className="hook-bg-deco" />
              <div className="hook-top">
                <div className={`hook-ico${h.accent ? ` ${h.accent}` : ''}`}>{h.icon}</div>
                <div className="hook-num">/ {h.n}</div>
              </div>
              <h3>{h.title}</h3>
              <p className="hook-sub">{h.sub}</p>
              <p className="body-text">{h.body}</p>
              <a href="#contact" className="hook-cta">{h.cta} <IArrowR size={14} /></a>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Pricing ──────────────────────────────────────────────────
const PRICES = [
  { amt: '$200', yr: '2010 – 2017', tier: 'Full-size sedan', desc: 'Reliable, fuel-efficient, perfect for daily commuting or gig work.', fine: 'Possibly not mint condition.', ribbon: null, feat: false },
  { amt: '$250', yr: '2011 – 2016', tier: 'Full-size sedan', sub: 'Honda Accord or similar', desc: 'Keyless entry, backup camera, modern comforts.', fine: null, ribbon: null, feat: false },
  { amt: '$280', yr: '2016 – 2019', tier: 'Toyota Avalon or older SUV', desc: 'Backup camera, spacious interior, premium comfort.', fine: null, ribbon: 'Most picked', feat: true },
  { amt: '$300', yr: '2019 –', tier: 'Standard SUV', desc: 'Apple CarPlay included. Extra cargo space, higher seating, family-friendly. Extra for 7-seater.', fine: null, ribbon: null, feat: false },
];

function Pricing() {
  return (
    <section id="pricing" className="ps-section">
      <div className="section-inner">
        <div className="section-head">
          <div>
            <span className="eyebrow"><span className="e-dot">$</span>Transparent weekly rates</span>
            <h2>One simple price.<br />Everything <em>included</em>.</h2>
          </div>
          <p className="lede">No payment until you see the car. Option to purchase the exact vehicle you're renting.</p>
        </div>

        <div className="pricing-promises">
          <div className="promise">
            <div className="p-ic"><ICheck size={16} /></div>
            <div><div className="p-t">All maintenance covered</div><div className="p-d">AC, transmission, engine — included.</div></div>
          </div>
          <div className="promise">
            <div className="p-ic"><IZap size={16} /></div>
            <div><div className="p-t">Same-day replacement</div><div className="p-d">Accident? You get another car the same day.</div></div>
          </div>
          <div className="promise">
            <div className="p-ic"><IShield size={16} /></div>
            <div><div className="p-t">We handle insurance claims</div><div className="p-d">Tegrat fronts the paperwork end-to-end.</div></div>
          </div>
        </div>

        <div className="pricing-grid">
          {PRICES.map((p, i) => (
            <div key={i} className={`price-card${p.feat ? ' featured' : ''}`}>
              {p.ribbon && <span className="ribbon">{p.ribbon}</span>}
              <div className="pc-yr">{p.yr}</div>
              <div className="pc-amount">{p.amt}<small>/week</small></div>
              <div className="pc-tier">{p.tier}{p.sub && <span className="pc-sub"> — {p.sub}</span>}</div>
              <p className="pc-desc">{p.desc}</p>
              {p.fine && <p className="pc-fine">{p.fine}</p>}
            </div>
          ))}
        </div>
        <p className="price-note">Subject to availability. Option to purchase the exact car you're renting.</p>
      </div>
    </section>
  );
}

// ── Requirements ─────────────────────────────────────────────
function Requirements() {
  return (
    <section id="requirements" className="ps-section">
      <div className="section-inner">
        <div className="requirements">
          <div className="req-stat"><IClock size={14} />Approved in <strong>15 min</strong></div>
          <div className="req-left">
            <span className="eyebrow"><span className="e-dot"><ICheck size={12} /></span>What you need to start</span>
            <h2>Simple paperwork.<br />On the road in <em>15 minutes</em>.</h2>
            <p>Our agent can help you start the paperwork before you even arrive at the office. Bring what's on the right and we'll handle the rest.</p>
            <ul className="req-checklist">
              <li><span className="tick"><ICheck size={13} /></span>Valid US or international driver's license</li>
              <li><span className="tick"><ICheck size={13} /></span>Full-coverage insurance policy</li>
              <li><span className="tick"><ICheck size={13} /></span>Proof of income (W-2, gig statement, etc.)</li>
              <li><span className="tick"><ICheck size={13} /></span>Refundable deposit</li>
            </ul>
          </div>
          <div className="insurance-card">
            <h4>Insurance policy details</h4>
            <div className="insurance-row">
              <div className="ir-ic"><IShield size={15} /></div>
              <div>
                <div className="ir-t">Progressive — guaranteed acceptance</div>
                <div className="ir-d">All active Progressive policies accepted, no questions asked.</div>
              </div>
            </div>
            <div className="insurance-row">
              <div className="ir-ic"><IShield size={15} /></div>
              <div>
                <div className="ir-t">Other carriers</div>
                <div className="ir-d">Our team will confirm coverage limits with you at the office.</div>
              </div>
            </div>
            <div className="insurance-row">
              <div className="ir-ic"><IShield size={15} /></div>
              <div>
                <div className="ir-t">Lienholder status</div>
                <div className="ir-d">Tegrat Auto must be officially added as a lienholder before release.</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Locations ────────────────────────────────────────────────
function Locations() {
  return (
    <section id="locations" className="ps-section">
      <div className="section-inner">
        <div className="section-head">
          <div>
            <span className="eyebrow"><span className="e-dot">◎</span>Find us</span>
            <h2>Five hubs.<br /><em>One reliable team.</em></h2>
          </div>
          <p className="lede">Walk into the office or call ahead — most pickups are wrapped in under an hour.</p>
        </div>
        <div className="locations-grid">
          {/* Indianapolis */}
          <article className="loc-card">
            <div className="loc-map">
              <iframe
                title="Tegrat Auto - Indianapolis"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3060.0962278064985!2d-86.1087732!3d39.916862699999996!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88131de9ea54a821%3A0x7835473573ad3822!2sTegrat%20Rental%20Car%20-%20Indianapolis!5e0!3m2!1sen!2sus!4v1766262320637!5m2!1sen!2sus"
                loading="lazy"
                allowFullScreen=""
              />
            </div>
            <div className="loc-body">
              <div className="loc-row1">
                <h3>Indianapolis</h3>
                <span className="loc-status"><span className="dot" />Open today</span>
              </div>
              <p className="loc-addr">8888 Keystone Crossing, Suite 1300</p>
              <div className="loc-contacts">
                <div className="lc-row"><span className="lc-name">Aye</span><a href="tel:+13173534274">(317) 353-4274</a></div>
                <div className="lc-row"><span className="lc-name">Nate</span><a href="tel:+13177099519">(317) 709-9519</a></div>
              </div>
            </div>
          </article>

          {/* Pittsburgh */}
          <article className="loc-card">
            <div className="loc-map">
              <iframe
                title="Tegrat Auto - Pittsburgh"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3036.8476311698305!2d-79.95542130000001!3d40.4343727!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8834ed4968c0bb69%3A0xe422ebc0c4a18e0c!2sTegrat%20Rental%20Car%20-%20Pittsburgh!5e0!3m2!1sen!2sus!4v1766262365937!5m2!1sen!2sus"
                loading="lazy"
                allowFullScreen=""
              />
            </div>
            <div className="loc-body">
              <div className="loc-row1">
                <h3>Pittsburgh</h3>
                <span className="loc-status"><span className="dot" />Open today</span>
              </div>
              <p className="loc-addr">3530 Boulevard of the Allies · Levi's garage</p>
              <div className="loc-contacts">
                <div className="lc-row"><span className="lc-name">Jamie</span><a href="tel:+14126894818">(412) 689-4818</a></div>
              </div>
            </div>
          </article>

          {/* Dallas / Plano */}
          <article className="loc-card">
            <div className="loc-map">
              <iframe
                title="Tegrat Auto - Dallas / Plano"
                src="https://maps.google.com/maps?q=4065%20Beacon%20Square%20Blvd%2C%20Plano%2C%20TX%2075075&output=embed"
                loading="lazy"
                allowFullScreen=""
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
            <div className="loc-body">
              <div className="loc-row1">
                <h3>Dallas / Plano</h3>
                <span className="loc-status"><span className="dot" />Open today</span>
              </div>
              <p className="loc-addr">4065 Beacon Square Blvd, Plano, TX 75075</p>
              <div className="loc-contacts">
                <div className="lc-row"><span className="lc-name">Chistel</span><a href="tel:+14693459170">(469) 345-9170</a></div>
                <div className="lc-row">
                  <span className="lc-name">Search "Tegrat Auto"</span>
                  <a href="https://turo.com/us/en/drivers/51618653" target="_blank" rel="noopener noreferrer">
                    View on Turo
                  </a>
                </div>
              </div>
            </div>
          </article>

          {/* West Lafayette */}
          <article className="loc-card">
            <div className="loc-map">
              <iframe
                title="Purdue University - West Lafayette"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d6074.658345380088!2d-86.92376952278242!3d40.42370945521275!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8812fd37423e0507%3A0x8eccb2cf8b1a7c8e!2sPurdue%20University!5e0!3m2!1sen!2sus!4v1778636730663!5m2!1sen!2sus"
                loading="lazy"
                allowFullScreen=""
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
            <div className="loc-body">
              <div className="loc-row1">
                <h3>West Lafayette, IN</h3>
                <span className="loc-status"><span className="dot" />Open today</span>
              </div>
              <p className="loc-addr">West Lafayette, Indiana</p>
              <div className="loc-contacts">
                <div className="lc-row"><span className="lc-name">Ben</span><a href="tel:+17657710368">(765) 771-0368</a></div>
              </div>
            </div>
          </article>

          {/* Los Angeles / Orange */}
          <article className="loc-card">
            <div className="loc-map">
              <iframe
                title="Tegrat Auto - Los Angeles / Orange"
                src="https://maps.google.com/maps?q=2050%20W%20Chapman%20Ave%2C%20Orange%2C%20CA%2092868&output=embed"
                loading="lazy"
                allowFullScreen=""
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
            <div className="loc-body">
              <div className="loc-row1">
                <h3>Los Angeles / Orange</h3>
                <span className="loc-status"><span className="dot" />Open today</span>
              </div>
              <p className="loc-addr">2050 W Chapman Ave, Suite 196, Orange, CA 92868</p>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
}

// ── Contact / Lead form ──────────────────────────────────────
function Contact({ onSubmit, form, onChange, status, error }) {
  return (
    <section id="contact" className="ps-section ps-contact">
      <div className="section-inner">
        <div className="contact-wrap">
          <div className="glow" />
          <div className="contact-head">
            <div>
              <span className="eyebrow">
                <span className="e-dot"><ISparkle size={12} /></span>Get on the road
              </span>
              <h2>Tell us what you need.<br /><em>We'll call you back today.</em></h2>
              <p className="lede">Send a few details and our team will follow up with the right vehicle and rate for your situation.</p>
            </div>
            <div className="contact-stats">
              <div className="cs-stat"><strong>&lt; 24 hrs</strong>response time</div>
              <div className="cs-stat"><strong>5 hubs</strong>across the US</div>
            </div>
          </div>

          <form onSubmit={onSubmit}>
            <div className="lead-form-grid">
              <div className="lf-field">
                <label htmlFor="f-name">Name</label>
                <input id="f-name" name="name" type="text" required value={form.name} onChange={onChange} autoComplete="name" />
              </div>
              <div className="lf-field">
                <label htmlFor="f-phone">Phone</label>
                <input id="f-phone" name="phone" type="tel" required placeholder="(555) 555-5555" value={form.phone} onChange={onChange} autoComplete="tel" />
              </div>
              <div className="lf-field">
                <label htmlFor="f-email">Email</label>
                <input id="f-email" name="email" type="email" placeholder="you@example.com" value={form.email} onChange={onChange} autoComplete="email" />
              </div>
              <div className="lf-field">
                <label htmlFor="f-loc">Nearest location</label>
                <select id="f-loc" name="location" required value={form.location} onChange={onChange}>
                  <option value="">Choose a location</option>
                  <option value="Indianapolis">Indianapolis</option>
                  <option value="Pittsburgh">Pittsburgh</option>
                  <option value="Dallas / Plano">Dallas / Plano</option>
                  <option value="West Lafayette">West Lafayette</option>
                  <option value="Los Angeles / Orange">Los Angeles / Orange</option>
                </select>
              </div>
              <div className="lf-field">
                <label htmlFor="f-int">I need help with</label>
                <select id="f-int" name="interest" required value={form.interest} onChange={onChange}>
                  <option value="">Choose one</option>
                  <option value="Rental">Rental</option>
                  <option value="Lease Buy-out">Lease Buy-out</option>
                  <option value="Car in shop">Car in shop</option>
                  <option value="Uber/Lyft/DoorDash">Uber / Lyft / DoorDash</option>
                  <option value="International license or student lease">International license or student lease</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="lf-field">
                <label htmlFor="f-pcm">Best way to reach you</label>
                <select id="f-pcm" name="preferred_contact_method" value={form.preferred_contact_method} onChange={onChange}>
                  <option value="phone">Phone call</option>
                  <option value="text">Text</option>
                  <option value="email">Email</option>
                </select>
              </div>
              <div className="lf-field wide">
                <label htmlFor="f-tf">When do you need a car?</label>
                <input id="f-tf" name="timeframe" type="text" placeholder="Today, this week, next month..." value={form.timeframe} onChange={onChange} />
              </div>
              <div className="lf-field wide">
                <label htmlFor="f-msg">Message</label>
                <textarea id="f-msg" name="message" rows="4" placeholder="Tell us anything that helps us prepare before we contact you." value={form.message} onChange={onChange} />
              </div>
              {/* Honeypot */}
              <div className="lf-field lf-hidden" aria-hidden="true">
                <label htmlFor="f-company">Company</label>
                <input id="f-company" name="company" type="text" value={form.company} onChange={onChange} tabIndex="-1" autoComplete="off" />
              </div>

              <div className="lead-submit">
                <p className="lead-mini">By submitting, you agree to be contacted by Tegrat Auto via your chosen method. We never share your info. <span style={{color:'var(--muted,#888)'}}>Response within 24 hours during business hours (ET).</span></p>
                <button type="submit" className="btn-submit" disabled={status === 'submitting'}>
                  {status === 'submitting' ? 'Sending…' : (<>Send request <IArrowR size={15} /></>)}
                </button>
              </div>
              {status === 'success' && (
                <p className="lead-alert success">Thanks. We received your request and will follow up soon.</p>
              )}
              {status === 'error' && (
                <p className="lead-alert error">{error}</p>
              )}
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

// ── iOS app prompt ───────────────────────────────────────────
function IosAppPrompt({ onClose, continueHref }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="app-prompt-overlay" onClick={onClose}>
      <div
        className="app-prompt"
        role="dialog"
        aria-modal="true"
        aria-labelledby="app-prompt-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="app-prompt-close" onClick={onClose} aria-label="Close">
          <I><path d="M6 6l12 12M18 6L6 18" /></I>
        </button>
        <img src={logoUrl} alt="" className="app-prompt-logo" width="56" height="56" />
        <h3 id="app-prompt-title">Get the Tegrat Auto app</h3>
        <p>
          You&rsquo;re on an iPhone or iPad. Our iOS app is the fastest way to see your balance,
          make a payment, and message your hub.
        </p>
        <a href={IOS_APP_URL} className="btn-blue btn-primary app-prompt-cta" target="_blank" rel="noopener noreferrer">
          Download on the App Store <IArrowR size={15} />
        </a>
        <a href={continueHref} className="app-prompt-skip">Continue in the browser</a>
      </div>
    </div>
  );
}

// ── Footer ───────────────────────────────────────────────────
function Footer() {
  const [showAppPrompt, setShowAppPrompt] = useState(false);

  // On iOS, nudge customers toward the app before sending them to the web dashboard.
  const handleSignIn = (e) => {
    if (!isIOSDevice()) return;
    e.preventDefault();
    setShowAppPrompt(true);
  };

  return (
    <footer className="ps-foot">
      <div className="foot-inner">
        <div className="foot-brand">
          <a href="#top" className="ps-nav-brand">
            <span className="ps-nav-mark">T</span>Tegrat Auto
          </a>
          <p>Reliable cars, simple paperwork, and a real path to ownership — across five US hubs.</p>
          <div className="foot-socials">
            <a href="https://www.instagram.com/tegratauto/" aria-label="Instagram" target="_blank" rel="noopener noreferrer"><IInsta size={16} /></a>
            <a href="mailto:info@tegratauto.com" aria-label="Email"><IMail size={16} /></a>
            <a href="tel:+13173534274" aria-label="Phone"><IPhone size={16} /></a>
          </div>
        </div>
        <div className="foot-col">
          <h5>Explore</h5>
          <a href="#services">Services</a>
          <a href="#pricing">Pricing</a>
          <a href="#requirements">Requirements</a>
          <a href="#locations">Locations</a>
          <a href="#invest">Invest with us</a>
        </div>
        <div className="foot-col">
          <h5>Customers</h5>
          <a href="/dashboard" onClick={handleSignIn}>Sign in</a>
          <a href="#contact">Request a car</a>
          <a href="/support">Support</a>
        </div>
        <div className="foot-col">
          <h5>Legal</h5>
          <a href="/sms-consent">SMS consent &amp; policy</a>
          <a href="/privacy">Privacy</a>
          <a href="#">Terms</a>
        </div>
      </div>
      <div className="foot-bottom">
        <div>© 2026 Tegrat Auto · © Des Dayton Digital LLC</div>
        <div><a href="#top">Back to top</a></div>
      </div>
      {showAppPrompt && (
        <IosAppPrompt continueHref="/dashboard" onClose={() => setShowAppPrompt(false)} />
      )}
    </footer>
  );
}

// ── Root component ───────────────────────────────────────────
const EMPTY_FORM = {
  name: '', phone: '', email: '', location: '', interest: '',
  preferred_contact_method: 'phone', timeframe: '', message: '', company: '',
};

function PublicSite() {
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState(EMPTY_FORM);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');

  useEffect(() => {
    const qrCode = searchParams.get('q');
    if (qrCode) {
      fetch(`${API_BASE_URL}/qr?code=${encodeURIComponent(qrCode)}`, { method: 'POST' })
        .catch(() => {});
    }
  }, [searchParams]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          source_code: searchParams.get('q') || searchParams.get('utm_campaign') || '',
          source_url: window.location.href,
        }),
      });
      const result = await res.json().catch(() => ({}));
      if (!res.ok || result.success === false) {
        throw new Error(result.error || 'Please check the form and try again.');
      }
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'conversion', {
          send_to: 'AW-17866890831/REPLACE_WITH_YOUR_CONVERSION_LABEL',
        });
      }
      setForm(EMPTY_FORM);
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setError(err.message || 'Something went wrong. Please call us directly.');
    }
  };

  return (
    <div className="ps-root">
      <div className="bg-deco" aria-hidden="true">
        <div className="dots" />
        <div className="blob b1" />
        <div className="blob b2" />
        <div className="blob b3" />
      </div>
      <Nav />
      <Hero />
      <Marquee />
      <Services />
      <Pricing />
      <Requirements />
      <Locations />
      <InvestSection />
      <Contact
        form={form}
        onChange={handleChange}
        onSubmit={handleSubmit}
        status={status}
        error={error}
      />
      <Footer />
    </div>
  );
}

export default PublicSite;
